import { ScrollView, ScrollViewRef } from "@/component/ScrollView";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { timeFromEvent } from "../../logic/utils";
import { TimelineTime, TimelineTrack } from "../../types";
import { ActiveTimeMarker } from "./ActiveTimeMarker";
import { Ruler } from "./Ruler";
import { TrackRow } from "./TrackRow";
import {
  TIMELINE_CONTENT_OFFSET,
  TIMELINE_PADDING,
  TRACK_ICON_WIDTH,
} from "./trackTheme";

export type TimelineTracksProps = {
  tracks: TimelineTrack[];
  trackWidth: number;
  activeTime: TimelineTime;
  totalDuration: TimelineTime;
  pixelsPerMs: number;
  onSeek: (time: TimelineTime) => void;
  onSelectItem?: (itemId: string) => void;
  registerContainer?: (node: HTMLDivElement | null) => void;
};

export function TimelineTracks({
  tracks,
  trackWidth,
  activeTime,
  totalDuration,
  pixelsPerMs,
  onSeek,
  onSelectItem,
  registerContainer,
}: TimelineTracksProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollViewRef = useRef<ScrollViewRef | null>(null);
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const contentWidth = trackWidth + TRACK_ICON_WIDTH + TIMELINE_PADDING;

  const handleContainerRef = useCallback(
    (node: HTMLElement | null) => {
      const element = node as HTMLDivElement | null;
      scrollContainerRef.current = element;
      setScrollNode(element);
      registerContainer?.(element);
    },
    [registerContainer]
  );

  const handleSeek = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const time = timeFromEvent(
        event as React.MouseEvent<HTMLDivElement>,
        scrollContainerRef.current,
        totalDuration,
        pixelsPerMs,
        TIMELINE_CONTENT_OFFSET
      );

      if (time != null) {
        onSeek(time);
      }
    },
    [onSeek, pixelsPerMs, totalDuration]
  );

  useEffect(() => {
    const node = scrollNode;
    if (!node) return;
    const handleScroll = () => setScrollLeft(node.scrollLeft);
    handleScroll();
    node.addEventListener("scroll", handleScroll, { passive: true });
    return () => node.removeEventListener("scroll", handleScroll);
  }, [scrollNode]);

  // 当 contentWidth 变化时，重新计算 ScrollView
  useEffect(() => {
    scrollViewRef.current?.recalculate();
  }, [contentWidth]);

  const tracksContent = useMemo(
    () => (
      <div className="min-w-full">
        <div className="sticky top-0 min-w-full z-30">
          <div
            className="relative min-w-full z-30 bg-background-elevated-2"
            style={{ width: contentWidth }}
          >
            <Ruler
              totalDuration={totalDuration}
              pixelsPerMs={pixelsPerMs}
              trackWidth={trackWidth}
            />
          </div>
        </div>
        {tracks.map((track, index) => (
          <TrackRow
            index={index}
            key={track.id}
            track={track}
            trackWidth={trackWidth}
            pixelsPerMs={pixelsPerMs}
            onSelectItem={onSelectItem}
          />
        ))}
      </div>
    ),
    [contentWidth, onSelectItem, pixelsPerMs, totalDuration, trackWidth, tracks]
  );

  return (
    <div className="relative flex bg-background-elevated-2 rounded-tl-xl rounded-tr-xl overflow-hidden">
      <ActiveTimeMarker
        activeTime={activeTime}
        scrollContainer={scrollContainerRef.current}
        totalDuration={totalDuration}
        pixelsPerMs={pixelsPerMs}
        scrollLeft={scrollLeft}
        onDrag={onSeek}
      />
      <ScrollView
        ref={scrollViewRef}
        className="w-full max-h-56 h-full"
        autoHide={false}
        onScrollableNode={handleContainerRef}
        onClick={handleSeek}
        scrollableNodeProps={{ className: "touch-pan-x" }}
      >
        {tracksContent}
      </ScrollView>
    </div>
  );
}

export { MAX_CANVAS_PX, RULER_HEIGHT, getRulerRenderConfig } from "./Ruler";
